import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";

import {
  useAddCaseItem,
  useCaseDetail,
  useEvidenceList,
} from "@/api/evidence";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryState } from "@/components/QueryState";

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error, refetch } = useCaseDetail(id ?? null);
  const { data: evidence } = useEvidenceList();
  const add = useAddCaseItem();
  const [picker, setPicker] = useState(false);
  const [note, setNote] = useState("");

  if (!id) return null;
  // T1a — `isLoading || !data` renders "Loading…" FOREVER on a failed fetch:
  // React Query sets isLoading=false and data=undefined, so this branch stays
  // true and nothing ever says the request failed. A spinner that never
  // resolves is the same false signal as an empty table.
  if (isError) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={error}
        label="this case"
        onRetry={refetch}
      >
        {null}
      </QueryState>
    );
  }
  if (isLoading || !data) return <p className="text-sm">Loading…</p>;

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/evidence">
          <ArrowLeftIcon className="mr-2 h-4 w-4" />Back to Evidence
        </Link>
      </Button>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{data.title}</h1>
        <p className="text-sm text-muted-foreground">{data.description ?? "—"}</p>
        <Badge variant={data.status === "open" ? "online" : "offline"}>
          {data.status}
        </Badge>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Items ({data.items.length})
            </h2>
            <Button size="sm" onClick={() => setPicker((x) => !x)}>
              <PlusIcon className="mr-2 h-4 w-4" />Add item
            </Button>
          </div>
          {picker && (
            <div className="space-y-2 rounded border p-3">
              <p className="text-xs text-muted-foreground">Pick evidence:</p>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                defaultValue=""
                onChange={async (e) => {
                  if (!e.target.value) return;
                  await add.mutateAsync({
                    caseId: id, evidence_id: e.target.value,
                  });
                  setPicker(false);
                }}
              >
                <option value="">— select evidence —</option>
                {(evidence?.items ?? []).map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.evidence_type}: {it.title}
                  </option>
                ))}
              </select>
              <p className="pt-2 text-xs text-muted-foreground">Or add a note:</p>
              <div className="flex gap-2">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Investigator note…"
                />
                <Button
                  size="sm"
                  disabled={!note || add.isPending}
                  onClick={async () => {
                    await add.mutateAsync({ caseId: id, note });
                    setNote("");
                    setPicker(false);
                  }}
                >Save</Button>
              </div>
            </div>
          )}
          <ul className="space-y-2">
            {data.items.map((it) => {
              // A7/AU-160 — resolve the evidence title from the already-loaded
              // list (was a bare truncated UUID) so each case item is auditable.
              const ev = it.evidence_id
                ? (evidence?.items ?? []).find((e) => e.id === it.evidence_id)
                : undefined;
              return (
                <li key={it.id} className="rounded border p-2 text-sm">
                  {ev ? (
                    <span className="font-medium">
                      {ev.evidence_type}: {ev.title}
                    </span>
                  ) : it.evidence_id ? (
                    <span className="font-mono text-xs">
                      Evidence: {it.evidence_id.slice(0, 8)}…
                    </span>
                  ) : null}
                  {it.clip_mark_id && (
                    <span className="font-mono text-xs">
                      Clip mark: {it.clip_mark_id.slice(0, 8)}…
                    </span>
                  )}
                  {it.note && <p>{it.note}</p>}
                  {/* A7/AU-160 — the auditable chain: who added it, and when. */}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Added by{" "}
                    <span className="font-mono">{it.added_by.slice(0, 8)}…</span>
                    {it.added_at
                      ? ` · ${new Date(String(it.added_at)).toLocaleString()}`
                      : ""}
                  </p>
                </li>
              );
            })}
            {data.items.length === 0 && (
              <li className="py-6 text-center text-sm text-muted-foreground">
                No items in this case yet.
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
